import React, { createContext, useContext, useMemo, useState } from "react";
import { useSelector, useDispatch, useStore } from "react-redux";
import { getUserFromLocalStorage } from "../utils/userLocalStorage";
import {
  setUser,
  setIsActiveDoctor,
  selectUser,
  selectIsActiveDoctor,
} from "../store/slices/authSlice";
import {
  setSidebarDrawerOpen,
  setDoctorMsgMobileBarShow,
  setSearchValues,
  setCookiesValue,
  selectSidebarDrawerOpen,
  selectDoctorMsgMobileBarShow,
  selectSearchValues,
  selectCookiesValue,
} from "../store/slices/uiSlice";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  let store = null;
  let dispatch = null;
  let reduxUser;
  let reduxIsActiveDocAcc;
  let reduxSidebarOpen;
  let reduxDoctorMsgMobileBarShow;
  let reduxSearchValues;
  let reduxCookiesValue;

  try {
    store = useStore();
    dispatch = useDispatch();
    reduxUser = useSelector(selectUser);
    reduxIsActiveDocAcc = useSelector(selectIsActiveDoctor);
    reduxSidebarOpen = useSelector(selectSidebarDrawerOpen);
    reduxDoctorMsgMobileBarShow = useSelector(selectDoctorMsgMobileBarShow);
    reduxSearchValues = useSelector(selectSearchValues);
    reduxCookiesValue = useSelector(selectCookiesValue);
  } catch {
    // Graceful fallback if mounted in an isolated context without Redux Provider
  }

  // Fallback local state if rendered outside Redux Provider (e.g. legacy tests)
  const [localUserData, setLocalUserData] = useState(getUserFromLocalStorage());
  const [localIsActiveDocAcc, setLocalIsActiveDocAcc] = useState(false);
  const [localSearchValues, setLocalSearchValues] = useState(
    JSON.parse(sessionStorage?.getItem("doctolikHomeSearchValue") || "null")
  );
  const [localCookiesValue, setLocalCookiesValue] = useState();
  const [localSidebarDrawerOpen, setLocalSidebarDrawerOpen] = useState(false);
  const [localDoctorMsgMobileBarShow, setLocalDoctorMsgMobileBarShow] = useState(false);

  const isReduxAvailable = Boolean(store && dispatch);

  const userData = isReduxAvailable ? reduxUser : localUserData;
  const isActiveDocAcc = isReduxAvailable ? reduxIsActiveDocAcc : localIsActiveDocAcc;
  const sidebarDrawerOpen = isReduxAvailable ? reduxSidebarOpen : localSidebarDrawerOpen;
  const doctorMsgMobileBarShow = isReduxAvailable ? reduxDoctorMsgMobileBarShow : localDoctorMsgMobileBarShow;
  const searchValuesContext = isReduxAvailable ? reduxSearchValues : localSearchValues;
  const cookiesValue = isReduxAvailable ? reduxCookiesValue : localCookiesValue;

  const setUserDataWrapper = (val) => {
    const nextVal = typeof val === 'function' ? val(userData) : val;
    if (isReduxAvailable) {
      dispatch(setUser(nextVal));
    } else {
      setLocalUserData(nextVal);
    }
  };

  const setIsActiveDocAccWrapper = (val) => {
    const nextVal = typeof val === 'function' ? val(isActiveDocAcc) : val;
    if (isReduxAvailable) {
      dispatch(setIsActiveDoctor(nextVal));
    } else {
      setLocalIsActiveDocAcc(nextVal);
    }
  };

  const setSidebarDrawerOpenWrapper = (val) => {
    const nextVal = typeof val === 'function' ? val(sidebarDrawerOpen) : val;
    if (isReduxAvailable) {
      dispatch(setSidebarDrawerOpen(nextVal));
    } else {
      setLocalSidebarDrawerOpen(nextVal);
    }
  };

  const setDoctorMsgMobileBarShowWrapper = (val) => {
    const nextVal = typeof val === 'function' ? val(doctorMsgMobileBarShow) : val;
    if (isReduxAvailable) {
      dispatch(setDoctorMsgMobileBarShow(nextVal));
    } else {
      setLocalDoctorMsgMobileBarShow(nextVal);
    }
  };

  const setSearchValuesContextWrapper = (val) => {
    const nextVal = typeof val === 'function' ? val(searchValuesContext) : val;
    if (isReduxAvailable) {
      dispatch(setSearchValues(nextVal));
    } else {
      setLocalSearchValues(nextVal);
    }
  };

  const setCookiesValueWrapper = (val) => {
    const nextVal = typeof val === 'function' ? val(cookiesValue) : val;
    if (isReduxAvailable) {
      dispatch(setCookiesValue(nextVal));
    } else {
      setLocalCookiesValue(nextVal);
    }
  };

  const value = useMemo(
    () => ({
      userData,
      setUserData: setUserDataWrapper,
      isActiveDocAcc,
      setIsActiveDocAcc: setIsActiveDocAccWrapper,
      searchValuesContext,
      setSearchValuesContext: setSearchValuesContextWrapper,
      cookiesValue,
      setCookiesValue: setCookiesValueWrapper,
      sidebarDrawerOpen,
      setSidebarDrawerOpen: setSidebarDrawerOpenWrapper,
      doctorMsgMobileBarShow,
      setDoctorMsgMobileBarShow: setDoctorMsgMobileBarShowWrapper,
    }),
    [
      userData,
      isActiveDocAcc,
      searchValuesContext,
      cookiesValue,
      sidebarDrawerOpen,
      doctorMsgMobileBarShow,
      isReduxAvailable,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export function useUserContext() {
  return useContext(UserContext);
}
